import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import PercentIcon from "@mui/icons-material/Percent";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../context/SnackbarContext";
import { clientService } from "../../services/client/clientService";
import { cupoService } from "../../services/cupo/cupoService";
import { Client, ClientType } from "../../types/api.types";

const CLIENT_PAGE = 0;
const CLIENT_ROW_PER_PAGE = 20;

type CupoClienteCreateFormData = {
  clientTypeID: string;
  clienteId: string | undefined;
  porcentaje: string;
};

const CuposClienteCreatePage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [tiposCliente, setTiposCliente] = useState<ClientType[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [tipoClienteOpen, setTipoClienteOpen] = useState(false);
  const [tipoClienteInputValue, setTipoClienteInputValue] = useState("");
  const [selectedTipoCliente, setSelectedTipoCliente] =
    useState<ClientType | null>(null);

  const [clientes, setClientes] = useState<Client[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [clienteOpen, setClienteOpen] = useState(false);
  const [clienteInputValue, setClienteInputValue] = useState("");
  const [selectedCliente, setSelectedCliente] = useState<Client | null>(null);

  useEffect(() => {
    if (!tipoClienteOpen) {
      return undefined;
    }

    let active = true;

    const fetchTipos = async () => {
      setLoadingTipos(true);
      try {
        const list = await cupoService.getAllTiposCliente();
        if (active) {
          setTiposCliente(Array.isArray(list) ? list : []);
        }
      } catch {
        if (active) {
          showSnackbar("Error al buscar tipos de cliente", "error", 4000);
          setTiposCliente([]);
        }
      } finally {
        if (active) {
          setLoadingTipos(false);
        }
      }
    };

    const delayDebounceFn = setTimeout(() => {
      void fetchTipos();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [tipoClienteOpen, showSnackbar]);

  const tipoClienteOptions = useMemo(() => {
    const q = tipoClienteInputValue.trim().toLowerCase();
    const filtered = q
      ? tiposCliente.filter((t) =>
          String(t.name ?? "")
            .toLowerCase()
            .includes(q),
        )
      : tiposCliente;
    const byId = new Map(filtered.map((t) => [String(t.clientTypeID), t]));
    if (
      selectedTipoCliente &&
      !byId.has(String(selectedTipoCliente.clientTypeID))
    ) {
      return [selectedTipoCliente, ...filtered];
    }
    return filtered;
  }, [tiposCliente, tipoClienteInputValue, selectedTipoCliente]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CupoClienteCreateFormData>({
    defaultValues: {
      clientTypeID: "",
      clienteId: undefined,
      porcentaje: "",
    },
  });

  const clientTypeID = watch("clientTypeID");

  const clienteOptions = useMemo(() => {
    const byId = new Map(clientes.map((c) => [c.clientID, c]));
    if (selectedCliente && !byId.has(selectedCliente.clientID)) {
      return [selectedCliente, ...clientes];
    }
    return clientes;
  }, [clientes, selectedCliente]);

  useEffect(() => {
    if (!clientTypeID) {
      setSelectedTipoCliente(null);
      setTipoClienteInputValue("");
    }
    setValue("clienteId", "");
    setSelectedCliente(null);
    setClienteInputValue("");
    setClientes([]);
  }, [clientTypeID, setValue]);

  useEffect(() => {
    let active = true;

    if (!clienteOpen || !clientTypeID) {
      return undefined;
    }

    const fetchClientes = async () => {
      setLoadingClientes(true);
      try {
        const res = await clientService.getClientsByType(
          CLIENT_PAGE,
          CLIENT_ROW_PER_PAGE,
          clientTypeID,
          clienteInputValue.trim() || undefined,
        );
        if (active) {
          setClientes(res?.clients ?? []);
        }
      } catch {
        if (active) {
          showSnackbar("Error al buscar clientes", "error", 4000);
          setClientes([]);
        }
      } finally {
        if (active) {
          setLoadingClientes(false);
        }
      }
    };

    const delayDebounceFn = setTimeout(() => {
      void fetchClientes();
    }, 300);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [clienteInputValue, clienteOpen, clientTypeID, showSnackbar]);

  const onSubmit = async (data: CupoClienteCreateFormData) => {
    const percentage = Number(String(data.porcentaje).replace(",", "."));
    try {
      await cupoService.insertClassificationByClient({
        clientId: data.clienteId?.trim() || undefined,
        clientTypeID: data.clientTypeID,
        percentage,
      });
      showSnackbar("Cupo registrado correctamente", "success", 3000);
      navigate("/cupos/cliente");
    } catch (e: any) {
      showSnackbar(
        e?.response?.data?.message ?? "Error al guardar el cupo",
        "error",
        4000,
      );
    }
  };

  const handleCancel = () => {
    navigate("/cupos/cliente");
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/home");
              }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/cupos/cliente");
              }}
            >
              Cupos por cliente
            </Link>
            <Typography color="text.primary">Crear cliente</Typography>
          </Breadcrumbs>

          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <Typography
              variant="h5"
              component="h1"
              fontWeight={600}
              gutterBottom
            >
              Crear cliente
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Elegí el tipo de cliente, un cliente del listado filtrado y el
              porcentaje de cupo (0 a 100).
            </Typography>
          </motion.div>

          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 3,
                rowGap: 3,
              }}
            >
              <Controller
                name="clientTypeID"
                control={control}
                rules={{ required: "Debés seleccionar un tipo de cliente" }}
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    open={tipoClienteOpen}
                    onOpen={() => setTipoClienteOpen(true)}
                    onClose={() => setTipoClienteOpen(false)}
                    options={tipoClienteOptions}
                    getOptionLabel={(option) => option.name}
                    loading={loadingTipos}
                    inputValue={tipoClienteInputValue}
                    value={
                      tipoClienteOptions.find(
                        (t) => String(t.clientTypeID) === field.value,
                      ) ||
                      selectedTipoCliente ||
                      null
                    }
                    onChange={(_, newValue) => {
                      field.onChange(
                        newValue ? String(newValue.clientTypeID) : "",
                      );
                      setSelectedTipoCliente(newValue);
                      setTipoClienteInputValue(newValue?.name ?? "");
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setTipoClienteInputValue(newInputValue);
                      }
                    }}
                    isOptionEqualToValue={(option, val) =>
                      String(option.clientTypeID) === String(val.clientTypeID)
                    }
                    noOptionsText="No se encontraron tipos de cliente"
                    loadingText="Buscando…"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de cliente"
                        InputLabelProps={{ required: true }}
                        inputProps={{
                          ...params.inputProps,
                          required: false,
                        }}
                        error={!!errors.clientTypeID}
                        helperText={
                          loadingTipos
                            ? "Cargando tipos…"
                            : errors.clientTypeID?.message
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingTipos ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="clienteId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    disabled={!clientTypeID}
                    open={clienteOpen}
                    onOpen={() => setClienteOpen(true)}
                    onClose={() => setClienteOpen(false)}
                    options={clienteOptions}
                    getOptionLabel={(option) =>
                      `${option.cuit} — ${option.comercialName}`
                    }
                    loading={loadingClientes}
                    inputValue={clienteInputValue}
                    value={
                      clienteOptions.find((c) => c.clientID === field.value) ||
                      selectedCliente ||
                      null
                    }
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.clientID ?? "");
                      setSelectedCliente(newValue);
                      setClienteInputValue(
                        newValue
                          ? `${newValue.cuit} — ${newValue.comercialName}`
                          : "",
                      );
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setClienteInputValue(newInputValue);
                      }
                    }}
                    isOptionEqualToValue={(option, val) =>
                      option.clientID === val.clientID
                    }
                    noOptionsText={
                      clientTypeID
                        ? "No se encontraron clientes"
                        : "Elegí primero un tipo de cliente"
                    }
                    loadingText="Buscando…"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente (opcional)"
                        helperText={
                          loadingClientes
                            ? "Buscando clientes…"
                            : errors.clienteId?.message
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingClientes ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />

              <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>
                <Controller
                  name="porcentaje"
                  control={control}
                  rules={{
                    required: "El porcentaje es obligatorio",
                    validate: (v) => {
                      const n = Number(String(v).replace(",", "."));
                      if (Number.isNaN(n)) return "Ingresá un número válido";
                      if (n < 0 || n > 100)
                        return "El valor debe estar entre 0 y 100";
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          field.onChange("");
                          return;
                        }
                        const n = Number(String(raw).replace(",", "."));
                        if (Number.isNaN(n)) {
                          field.onChange(raw);
                          return;
                        }
                        if (n > 100) {
                          field.onChange("100");
                          return;
                        }
                        if (n < 0) {
                          field.onChange("0");
                          return;
                        }
                        field.onChange(raw);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                      fullWidth
                      type="number"
                      label="Porcentaje de cupo"
                      required
                      inputProps={{ step: 1 }}
                      error={!!errors.porcentaje}
                      helperText={errors.porcentaje?.message ?? "Entre 0 y 100"}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ display: "flex", mr: 0.5 }}>
                            <PercentIcon color="action" fontSize="small" />
                          </Box>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ minWidth: 120 }}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: 140 }}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Guardar
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CuposClienteCreatePage;
